/**
 * Custom-Event-Constants für loose Cross-Component-Decoupling.
 *
 * Pattern aus `react.md` § "Custom-Events als loose Cross-Component-
 * Decoupling-Primitive (1-listener UI-trigger ohne Context-Pollution)".
 *
 * Naming-Convention: `gf:<noun>-<verb-past-participle>`. `gf:`-Prefix
 * verhindert Modul-Name-Collisions auf `window`-global EventTarget.
 */
export const AUTH_USER_CHANGED = 'gf:auth-user-changed' as const;
/**
 * Fired wenn Trash-Inhalt sich ändert (soft-delete, restore, permanent-delete).
 * Sidebar listener refetcht `/api/assets/stats` für aktualisierten Trash-
 * Counter-Pill. Dispatched in 4 Components POST-success, nicht optimistic:
 *  - AssetInspector (single soft-delete)
 *  - BulkDeleteConfirm (bulk soft-delete)
 *  - TrashActionBar (bulk restore)
 *  - TrashPermanentDeleteConfirm (bulk permanent-delete)
 */
export const TRASH_CHANGED = 'gf:trash-changed' as const;
/**
 * Fired wenn Collections-Liste oder einzelne Collection sich ändert
 * (create / update / delete / member-add / member-remove). Konsumer:
 *  - CollectionListPage (refetch der Liste nach mutations aus anderen Pages)
 *  - AssetInspector CollectionPicker (refetch collection-list nach create
 *    in einem anderen Tab / einer anderen Page)
 */
export const COLLECTIONS_CHANGED = 'gf:collections-changed' as const;
/**
 * Fired wenn die Rezepte-Liste oder ein einzelnes Rezept sich ändert
 * (create / update / delete / asset-add / asset-remove). Konsumer:
 *  - RecipeListPage (refetch der Browse-Liste nach mutations)
 *  - RecipePicker (refetch des Such-Endpoints nach inline-create)
 */
export const RECIPES_CHANGED = 'gf:recipes-changed' as const;
/**
 * Sprint C4 — Fired nach jeder client-seitigen notification-mutation
 * (mark-as-read, read-all). Konsumer: `useNotificationsPoll` hook in
 * NotificationsPanel + Topbar Bell-counter → authoritative refetch
 * (A19 — server bleibt source-of-truth, KEIN lokal-decrement).
 *
 * NICHT dispatchen für inbound notifications (die kommen via polling).
 */
export const NOTIFICATIONS_CHANGED = 'gf:notifications-changed' as const;
/**
 * Fired nach erfolgreichem Asset-Upload (≥1 Datei neu in der DB). Konsumer:
 *  - LibraryPage (refetch der Asset-Liste, damit neue Uploads sofort erscheinen)
 *  - Sidebar (refetch `/api/assets/stats` → Storage-Bar + Kategorie-Counter)
 * Dispatched vom UploadModal POST-success (nicht optimistic).
 */
export const ASSETS_CHANGED = 'gf:assets-changed' as const;
/**
 * Roadmap 3a Phase 2 — Fired nach erfolgreichem ⭐-Favoriten-Toggle (PUT/DELETE).
 * Dispatched von `useFavoriteToggle` POST-success (nicht optimistic). Im Gegensatz
 * zu den anderen Events trägt dieses ein `detail: { assetId, favorited }` — der
 * einzige Konsument (`FavoritesPage`) entfernt damit die un-favorisierte Karte
 * instant aus der lokalen Liste (ohne Refetch). Listener castet
 * `(e as CustomEvent<FavoritesChangedDetail>).detail`.
 */
export const FAVORITES_CHANGED = 'gf:favorites-changed' as const;
export type FavoritesChangedDetail = { assetId: string; favorited: boolean };
